import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ShieldCheck, 
  Plus, 
  Trash2, 
  Loader2,
  ArrowLeft,
  UserPlus,
  Shield
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatDateDisplay } from '../../utils/dateUtils';

const SystemAdminsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    full_name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admin/system-admins');
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to load system admins');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAdmin.full_name || !newAdmin.username || !newAdmin.email || !newAdmin.password) {
      toast.error('All fields are required');
      return;
    }
    
    if (newAdmin.password !== newAdmin.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newAdmin.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setFormLoading(true);
    try {
      await api.post('/admin/system-admins', {
        full_name: newAdmin.full_name,
        username: newAdmin.username,
        email: newAdmin.email,
        password: newAdmin.password
      });
      toast.success('System admin created successfully');
      setCreateDialogOpen(false);
      setNewAdmin({ full_name: '', username: '', email: '', password: '', confirmPassword: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create system admin');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;
    
    setFormLoading(true);
    try {
      await api.delete(`/admin/system-admins/${selectedAdmin.id}`);
      toast.success('System admin deleted');
      setDeleteDialogOpen(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete system admin');
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="system-admins-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="font-outfit text-3xl font-bold text-slate-900">System Administrators</h1>
            <p className="text-slate-500 mt-1">Manage system admin accounts</p>
          </div>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="create-admin-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add System Admin
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              System Admins
            </CardTitle>
            <CardDescription>{admins.length} system administrator(s)</CardDescription>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No system admins found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>MFA Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id} data-testid={`admin-${admin.id}`}>
                      <TableCell className="font-medium">{admin.full_name}</TableCell>
                      <TableCell>{admin.username}</TableCell>
                      <TableCell>
                        {admin.mfa_enabled ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Enabled</Badge>
                        ) : (
                          <Badge variant="outline">Not Set</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatDateDisplay(admin.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {admin.id !== user?.id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => {
                              setSelectedAdmin(admin);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-admin-${admin.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Badge variant="outline" className="text-xs">Current User</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Admin Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-indigo-600" />
                Add System Admin
              </DialogTitle>
              <DialogDescription>
                Create a new system administrator account
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={newAdmin.full_name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, full_name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="admin-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  placeholder="jsmith"
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  placeholder="jsmith@example.com"
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                  data-testid="admin-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={newAdmin.confirmPassword}
                  onChange={(e) => setNewAdmin({ ...newAdmin, confirmPassword: e.target.value })}
                  placeholder="Re-enter password"
                  data-testid="admin-confirm-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCreate}
                disabled={formLoading}
                data-testid="save-admin-btn"
              >
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Admin'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-rose-600">Delete System Admin</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{selectedAdmin?.full_name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={formLoading}
                data-testid="confirm-delete-btn"
              >
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SystemAdminsPage;
